/**
 * CommunitiesContext — global state for the Communities feature.
 * Local mock state only (no backend) — mirrors the Bookshelf feature's approach.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/useAuth';
import {
  COMMUNITIES, MEMBERS_BY_COMMUNITY, POSTS, EVENTS, CHALLENGES, NOTIFICATIONS,
  CHAT_MESSAGES_BY_COMMUNITY, CURRENT_USER_ID, DEFAULT_RULES,
} from '../constants/seedData';

const CommunitiesContext = createContext(undefined);

function normalizeCommunity(community) {
  return {
    ...community,
    id: community.id ?? community.communityId,
    name: community.name ?? 'Untitled community',
    tagline: community.tagline ?? community.description ?? '',
    description: community.description ?? community.tagline ?? '',
    type: community.type ?? 'reader',
    visibility: community.visibility ?? 'public',
    tone: community.tone ?? 'coral',
    category: community.category ?? 'Genres',
    memberCount: community.memberCount ?? community.member_count ?? community.membersCount ?? 0,
    activeNow: community.activeNow ?? community.active_now ?? 0,
    createdAt: community.createdAt ?? community.created_at ?? Date.now(),
    tags: community.tags ?? [],
    rules: community.rules ?? [],
    joinStatus: community.viewerJoinStatus ?? community.joinStatus ?? (community.isMember || community.joined ? 'joined' : 'none'),
    role: community.role ?? null,
  };
}

export function CommunitiesProvider({ children }) {
  const location = useLocation();
  const { user, getCommunities, createCommunityApi, getCommunityDetails, joinCommunityApi, getCommunityEvents, createCommunityEvent, getCommunityMembers } = useAuth();
  const [communities, setCommunities] = useState(COMMUNITIES);
  const [membersByCommunity, setMembersByCommunity] = useState(MEMBERS_BY_COMMUNITY);
  const [posts, setPosts] = useState(POSTS);
  const [events, setEvents] = useState(EVENTS);
  const [challenges] = useState(CHALLENGES);
  const [notifications, setNotifications] = useState(NOTIFICATIONS);
  const [chatByCommunity, setChatByCommunity] = useState(CHAT_MESSAGES_BY_COMMUNITY);

  useEffect(() => {
    if (!user) return undefined;
    let active = true;
    getCommunities()
      .then((payload) => {
        if (!active) return;
        const result = payload?.data ?? payload;
        const list = result?.communities || result?.items || result?.results || (Array.isArray(result) ? result : []);
        setCommunities(list.map(normalizeCommunity).filter((community) => community.id));
      })
      .catch((error) => {
        console.error('[Communities API] Failed to load communities:', error);
      });
    return () => { active = false; };
  }, [getCommunities, user]);

  useEffect(() => {
    const communityId = location.pathname.match(/\/app\/communities\/([^/]+)/)?.[1];
    if (!user || !communityId) return undefined;
    let active = true;
    getCommunityDetails(communityId)
      .then((payload) => {
        if (!active) return;
        const result = payload?.data?.community || payload?.data || payload?.community || payload;
        if (!result?.id && !result?.communityId) return;
        const detail = normalizeCommunity(result);
        setCommunities((previous) => previous.some((community) => community.id === detail.id)
          ? previous.map((community) => community.id === detail.id ? { ...community, ...detail } : community)
          : [detail, ...previous]);
      })
      .catch((error) => console.error('[Communities API] Failed to load community details:', error));
    return () => { active = false; };
  }, [getCommunityDetails, location.pathname, user]);

  const getCommunity = useCallback((id) => communities.find(c => c.id === id), [communities]);
  const getMembers = useCallback((id) => membersByCommunity[id] ?? [], [membersByCommunity]);

  const loadCommunityMembers = useCallback(async (communityId) => {
    const payload = await getCommunityMembers(communityId);
    const result = payload?.data ?? payload;
    const list = result?.members || result?.items || result?.results || (Array.isArray(result) ? result : []);
    const normalized = list.map((member) => {
      const user = member.user || member;
      const name = user.displayName || user.name || user.username || 'Reader';
      return {
        ...user,
        ...member,
        id: user.id ?? user.userId ?? member.id ?? member.userId,
        name,
        initial: user.initial || name.charAt(0).toUpperCase(),
        tone: user.tone || user.avatarTone || 'coral',
        role: member.role || user.role || 'member',
        joinStatus: member.joinStatus || user.joinStatus || 'joined',
        joinedAt: member.joinedAt || user.joinedAt || null,
        booksRead: user.booksRead ?? user.books_read ?? 0,
      };
    }).filter((member) => member.id);
    setMembersByCommunity((previous) => ({ ...previous, [communityId]: normalized }));
    return normalized;
  }, [getCommunityMembers]);
  const getPosts = useCallback((id) => posts.filter(p => p.communityId === id), [posts]);
  const getEvents = useCallback((id) => events.filter(e => e.communityId === id), [events]);
  const getChallenge = useCallback((id) => challenges.find(c => c.communityId === id), [challenges]);
  const getChat = useCallback((id) => chatByCommunity[id] ?? [], [chatByCommunity]);
  const getRole = useCallback((id) => getCommunity(id)?.role ?? null, [getCommunity]);

  const joinCommunity = useCallback(async (id) => {
    const payload = await joinCommunityApi(id);
    const result = payload?.data?.community || payload?.data || payload?.community || {};
    setCommunities(prev => prev.map(c => {
      if (c.id !== id) return c;
      return { ...c, ...normalizeCommunity(result), joinStatus: result.joinStatus ?? 'joined', role: result.role ?? 'member', memberCount: result.memberCount ?? c.memberCount + 1 };
    }));
    return payload;
  }, [joinCommunityApi]);

  const leaveCommunity = useCallback((id) => {
    setCommunities(prev => prev.map(c => (
      c.id === id ? { ...c, joinStatus: 'none', role: null, memberCount: Math.max(0, c.memberCount - 1) } : c
    )));
  }, []);

  const cancelRequest = useCallback((id) => {
    setCommunities(prev => prev.map(c => (c.id === id ? { ...c, joinStatus: 'none' } : c)));
  }, []);

  const createCommunity = useCallback(async (data) => {
    const payload = await createCommunityApi(data);
    const result = payload?.data?.community || payload?.data || payload?.community || payload;
    const newCommunity = normalizeCommunity({ ...data, ...result, joinStatus: result.joinStatus ?? 'joined', role: result.role ?? 'admin' });
    setCommunities(prev => [newCommunity, ...prev]);
    setMembersByCommunity(prev => ({ ...prev, [newCommunity.id]: [{ id: CURRENT_USER_ID, name: user?.name ?? 'You', initial: (user?.name ?? 'Y')[0], tone: data.tone ?? 'coral', role: 'admin', booksRead: 0 }] }));
    return newCommunity;
  }, [createCommunityApi, user]);

  const addPost = useCallback((communityId, postData) => {
    const newPost = {
      id: `post_${Date.now()}`, communityId, createdAt: Date.now(),
      author: { name: 'Sakshi', initial: 'S', tone: 'coral', role: getRole(communityId) ?? 'member' },
      likes: 0, liked: false, saved: false, comments: [], pinned: false, locked: false,
      ...postData,
    };
    setPosts(prev => [newPost, ...prev]);
    return newPost;
  }, [getRole]);

  const toggleLike = useCallback((postId) => {
    setPosts(prev => prev.map(p => (p.id === postId ? { ...p, liked: !p.liked, likes: p.likes + (p.liked ? -1 : 1) } : p)));
  }, []);

  const toggleSave = useCallback((postId) => {
    setPosts(prev => prev.map(p => (p.id === postId ? { ...p, saved: !p.saved } : p)));
  }, []);

  const addComment = useCallback((postId, body, isSpoiler = false) => {
    setPosts(prev => prev.map(p => (
      p.id === postId
        ? { ...p, comments: [...p.comments, { id: `c_${Date.now()}`, author: { name: 'Sakshi', initial: 'S', tone: 'coral' }, body, isSpoiler, createdAt: Date.now() }] }
        : p
    )));
  }, []);

  const togglePin = useCallback((postId) => {
    setPosts(prev => prev.map(p => (p.id === postId ? { ...p, pinned: !p.pinned } : p)));
  }, []);

  const toggleLock = useCallback((postId) => {
    setPosts(prev => prev.map(p => (p.id === postId ? { ...p, locked: !p.locked } : p)));
  }, []);

  const removePost = useCallback((postId) => {
    setPosts(prev => prev.filter(p => p.id !== postId));
  }, []);

  const toggleEventJoin = useCallback((eventId) => {
    setEvents(prev => prev.map(e => (
      e.id === eventId ? { ...e, joined: !e.joined, interested: e.interested + (e.joined ? -1 : 1) } : e
    )));
  }, []);

  const loadCommunityEvents = useCallback(async (communityId) => {
    const payload = await getCommunityEvents(communityId);
    const result = payload?.data ?? payload;
    const list = result?.events || result?.items || result?.results || (Array.isArray(result) ? result : []);
    const normalized = list.map((event) => ({
      ...event,
      id: event.id ?? event.eventId,
      communityId,
      datetime: event.eventAt ? new Date(event.eventAt).getTime() : (event.datetime ?? event.event_at),
      interested: event.interested ?? event.interestedCount ?? event.interested_count ?? 0,
      joined: event.joined ?? event.isJoined ?? false,
      book: event.book ?? null,
    })).filter((event) => event.id);
    setEvents((previous) => [...previous.filter((event) => event.communityId !== communityId), ...normalized]);
    return normalized;
  }, [getCommunityEvents]);

  const createEvent = useCallback(async (communityId, data) => {
    const payload = await createCommunityEvent(communityId, data);
    const result = payload?.data?.event || payload?.data || payload?.event || payload;
    const newEvent = {
      ...data,
      ...result,
      id: result.id ?? result.eventId,
      communityId,
      datetime: result.eventAt ? new Date(result.eventAt).getTime() : data.eventAt ? new Date(data.eventAt).getTime() : data.datetime,
      interested: result.interested ?? 0,
      joined: result.joined ?? false,
    };
    setEvents((previous) => [newEvent, ...previous.filter((event) => event.id !== newEvent.id)]);
    return newEvent;
  }, [createCommunityEvent]);

  const sendChatMessage = useCallback((communityId, text) => {
    setChatByCommunity(prev => ({
      ...prev,
      [communityId]: [...(prev[communityId] ?? []), { id: `m_${Date.now()}`, author: { name: 'Sakshi', initial: 'S', tone: 'coral' }, text, createdAt: Date.now() }],
    }));
  }, []);

  const markNotificationsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, unread: false })));
  }, []);

  const setMemberRole = useCallback((communityId, memberId, role) => {
    setMembersByCommunity(prev => ({
      ...prev,
      [communityId]: (prev[communityId] ?? []).map(m => (m.id === memberId ? { ...m, role } : m)),
    }));
  }, []);

  const updateRules = useCallback((communityId, rules) => {
    setCommunities(prev => prev.map(c => (c.id === communityId ? { ...c, rules } : c)));
  }, []);

  const removeMember = useCallback((communityId, memberId) => {
    setMembersByCommunity(prev => ({
      ...prev,
      [communityId]: (prev[communityId] ?? []).filter(m => m.id !== memberId),
    }));
  }, []);

  const myCommunities = useMemo(() => communities.filter(c => c.joinStatus === 'joined'), [communities]);

  const trendingCommunities = useMemo(
    () => [...communities].sort((a, b) => b.activeNow - a.activeNow).slice(0, 6),
    [communities]
  );

  const popularCommunities = useMemo(
    () => [...communities].sort((a, b) => b.memberCount - a.memberCount).slice(0, 6),
    [communities]
  );

  const recentlyActiveCommunities = useMemo(() => {
    const lastPostAt = {};
    posts.forEach(p => {
      if (!lastPostAt[p.communityId] || p.createdAt > lastPostAt[p.communityId]) lastPostAt[p.communityId] = p.createdAt;
    });
    return [...communities]
      .filter(c => lastPostAt[c.id])
      .sort((a, b) => lastPostAt[b.id] - lastPostAt[a.id])
      .slice(0, 6);
  }, [communities, posts]);

  const recommendedCommunities = useMemo(() => {
    const joinedCategories = new Set(myCommunities.map(c => c.category));
    return communities
      .filter(c => c.joinStatus !== 'joined' && joinedCategories.has(c.category))
      .slice(0, 4)
      .map(c => ({ ...c, reason: `Because you like ${c.category}` }));
  }, [communities, myCommunities]);

  const unreadNotificationCount = notifications.filter(n => n.unread).length;

  const value = {
    communities, myCommunities, trendingCommunities, popularCommunities, recentlyActiveCommunities, recommendedCommunities,
    getCommunity, getMembers, loadCommunityMembers, getPosts, getEvents, getChallenge, getChat, getRole,
    joinCommunity, leaveCommunity, cancelRequest, createCommunity,
    addPost, toggleLike, toggleSave, addComment, togglePin, toggleLock, removePost,
    toggleEventJoin, createEvent, loadCommunityEvents, sendChatMessage,
    notifications, unreadNotificationCount, markNotificationsRead,
    setMemberRole, removeMember, updateRules,
  };

  return <CommunitiesContext.Provider value={value}>{children}</CommunitiesContext.Provider>;
}

export function useCommunities() {
  const ctx = useContext(CommunitiesContext);
  if (!ctx) throw new Error('useCommunities must be used within CommunitiesProvider');
  return ctx;
}
